import { b64Vecs, TLDrawShapeSegment, TLShapeId, Vec, VecModel } from "tldraw";
import { asColor } from "../../shared/format/FocusedColor";
import { convertFocusedFillToTldrawFill } from "../../shared/format/FocusedFill";
import { PenAction } from "../../shared/schema/AgentActionSchemas";
import { Streaming } from "../../shared/types/Streaming";
import { AgentHelpers } from "../AgentHelpers";
import { AgentActionUtil, registerActionUtil } from "./AgentActionUtil";
import { getPremadeStackingIndex } from "../utils/premadeZOrder";
import {
  AUTHORED_SOURCE,
  isAuthoredContentAction,
} from "../utils/authoredContent";
import { annotationMeta } from "../utils/annotationContent";

export const PenActionUtil = registerActionUtil(
  class PenActionUtil extends AgentActionUtil<PenAction> {
    static override type = "pen" as const;

    override getInfo(action: Streaming<PenAction>) {
      return {
        icon: "pencil" as const,
        description: action.intent ?? "",
      };
    }

    override sanitizeAction(
      action: Streaming<PenAction>,
      helpers: AgentHelpers,
    ) {
      if (!action.points) return action;

      // Ensure the shape has a unique ID
      action.shapeId = helpers.ensureShapeIdIsUnique(action.shapeId);

      // This is a complex action for the model, so validate the data it gives us
      const validPoints = action.points
        .map((point) => helpers.ensureValueIsVec(point))
        .filter((v) => v !== null);

      action.points = validPoints;
      action.closed = helpers.ensureValueIsBoolean(action.closed) ?? false;
      action.fill = helpers.ensureValueIsFocusedFill(action.fill) ?? "none";

      return action;
    }

    override applyAction(action: Streaming<PenAction>) {
      if (!action.points) return;
      if (action.points.length === 0) return;

      if (!action.shapeId) return;
      const shapeId = `shape:${action.shapeId}` as TLShapeId;

      if (action.closed) {
        const firstPoint = action.points[0];
        action.points.push(firstPoint);
      }

      // Ensure at least 2 source points before interpolation — a single
      // point fed into the loop below produces zero output because the loop
      // iterates `length - 1` times.  Duplicating up-front means the loop
      // always emits at least one point, and the post-loop guard below
      // brings it to the ≥2 minimum tldraw requires.
      if (action.points.length === 1) {
        action.points.push({ ...action.points[0] });
      }

      const minX = Math.min(...action.points.map((p) => p.x));
      const minY = Math.min(...action.points.map((p) => p.y));

      const points: VecModel[] = [];
      const maxDistanceBetweenPoints = action.style === "smooth" ? 10 : 2;
      for (let i = 0; i < action.points.length - 1; i++) {
        const point = action.points[i];
        points.push(point);

        const nextPoint = action.points[i + 1];
        if (!nextPoint) continue;

        const distance = Vec.Dist(point, nextPoint);
        const numPointsToAdd = Math.floor(distance / maxDistanceBetweenPoints);
        const pointsToAdd = Array.from({ length: numPointsToAdd }, (_, j) => {
          const t = (j + 1) / (numPointsToAdd + 1);
          return Vec.Lrp(point, nextPoint, t);
        });
        points.push(...pointsToAdd);
      }

      // Tldraw's DrawShapeUtil.getGeometry crashes with
      // "Polyline2d: points must be an array of at least 2 points" if
      // segments contain fewer than 2 points. Duplicate a single point
      // so the shape renders as a dot rather than crashing the canvas.
      if (points.length === 0) {
        return;
      }
      if (points.length === 1) {
        points.push({ ...points[0] });
      }

      const segmentPoints = points.map((point) => ({
        x: point.x - minX,
        y: point.y - minY,
        z: 0.75,
      }));
      const base64Points = b64Vecs.encodePoints(segmentPoints);

      const segments: TLDrawShapeSegment[] = [
        {
          type: "free",
          path: base64Points,
        },
      ];

      const isAuthored = isAuthoredContentAction(action);
      const premadeZIndex =
        isAuthored && typeof action.zIndex === "number"
          ? action.zIndex
          : undefined;
      const stackingIndex =
        premadeZIndex !== undefined
          ? getPremadeStackingIndex(this.editor, premadeZIndex)
          : undefined;

      this.editor.createShape({
        id: shapeId,
        type: "draw",
        x: minX,
        y: minY,
        rotation: action.rotation ?? 0,
        opacity: action.opacity ?? 1,
        // Authored strokes stay locked: the student may not drag the problem's
        // diagram around, while the agent still can (`ignoreShapeLock`).
        isLocked: isAuthored,
        // AI authorship stamp — see CreateActionUtil. Authored strokes are
        // tagged for the student delete-guard and layered by their zIndex.
        meta: {
          role: "agent",
          // Tick bars, underlines and right-angle markers arrive as pen strokes.
          ...annotationMeta(action),
          ...(isAuthored
            ? {
                source: AUTHORED_SOURCE,
                ...(premadeZIndex !== undefined
                  ? { zIndex: premadeZIndex }
                  : {}),
              }
            : {}),
        },
        ...(stackingIndex ? { index: stackingIndex } : {}),
        props: {
          color: asColor(action.color ?? "black"),
          fill: convertFocusedFillToTldrawFill(action.fill ?? "none"),
          dash: action.dash ?? "draw",
          size: action.size ?? "s",
          segments,
          isComplete: true,
          isClosed: action.closed,
          isPen: true,
        },
      });
    }
  },
);
